import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DocumentFilters as DocumentFiltersType } from '../../components/DocumentFilters';
import backend from '../../services/backend';
import { DOCUMENTS_AUTO_RELOAD_MS, DOCUMENTS_ITEMS_PER_PAGE } from './documentsConstants';
import { useInfiniteList } from '../../hooks/useInfiniteList';

const depthDescriptions: Record<1 | 2 | 3 | 4, string> = {
  1: 'classification data',
  2: 'embeddings and classification data',
  3: 'text, embeddings, and classification data',
  4: 'pages, text, embeddings, and classification data',
};

export type DocumentsPendingConfirm = 'reset' | 'delete' | 'removeCategory' | null;

export function useDocumentsList() {
  const [filters, setFilters] = useState<DocumentFiltersType>();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingConfirm, setPendingConfirm] = useState<DocumentsPendingConfirm>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [isDangerZoneExpanded, setIsDangerZoneExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [resetDepth, setResetDepth] = useState<1 | 2 | 3 | 4>(1);
  const [searchParams] = useSearchParams();
  const [isRetryingFailedSteps, setIsRetryingFailedSteps] = useState(false);

  const onlyIds = searchParams.getAll('ids');
  const uploadId = searchParams.get('uploadId');
  const attention = searchParams.get('attention') === '1' || searchParams.get('attention') === 'true';

  const queryKey = useMemo(
    () => ['documents', filters, Array.from(selectedIds).sort(), onlyIds, uploadId, attention],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, selectedIds, onlyIds.join(','), uploadId, attention],
  );

  const {
    items: documents,
    hasMore: hasNextPage,
    loading,
    loadMore,
    lastFetched,
    refetch: fetchDocuments,
  } = useInfiniteList({
    queryKey,
    queryFn: (limit) =>
      backend
        .listDocuments(1, limit, {
          ...(attention
            ? { attention: true }
            : {
                ...filters!,
              }),
          ids: selectedIds.size > 0 ? Array.from(selectedIds) : undefined,
          onlyIds: onlyIds.length > 0 ? Array.from(onlyIds) : undefined,
          uploadId: uploadId ?? undefined,
        })
        .then((res) => res.data),
    enabled: !!filters,
    initialItemsPerPage: DOCUMENTS_ITEMS_PER_PAGE,
    autoRefreshMs: DOCUMENTS_AUTO_RELOAD_MS,
    paused: isResetting,
    resetKey: filters,
  });

  const handleResetSelected = () => {
    if (selectedIds.size === 0) return;
    setPendingConfirm('reset');
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return;
    setPendingConfirm('delete');
  };

  const confirmResetSelected = async () => {
    try {
      setIsResetting(true);
      setResetError(null);
      await backend.resetDocuments(resetDepth, Array.from(selectedIds));
      await fetchDocuments();
      setIsDangerZoneExpanded(false);
    } catch (error) {
      console.error('Failed to reset documents:', error);
      setResetError('Failed to reset documents');
    } finally {
      setIsResetting(false);
      setPendingConfirm(null);
    }
  };

  const confirmDeleteSelected = async () => {
    try {
      setIsDeleting(true);
      setDeleteError(null);

      await Promise.all(Array.from(selectedIds).map((id) => backend.deleteDocument(id)));

      await fetchDocuments();
      setSelectedIds(new Set());
      setIsDangerZoneExpanded(false);
    } catch (error) {
      console.error('Failed to delete documents:', error);
      setDeleteError('Failed to delete documents');
    } finally {
      setIsDeleting(false);
      setPendingConfirm(null);
    }
  };

  const confirmRemoveCategory = async () => {
    try {
      setIsUpdatingCategory(true);
      setCategoryError(null);

      await Promise.all(Array.from(selectedIds).map((id) => backend.updateDocument(id, { category: null })));

      await fetchDocuments();
      setSelectedCategory('');
    } catch (error) {
      console.error('Failed to remove categories:', error);
      setCategoryError('Failed to remove categories');
    } finally {
      setIsUpdatingCategory(false);
      setPendingConfirm(null);
    }
  };

  const confirmDialogProps = {
    open: pendingConfirm !== null,
    title:
      pendingConfirm === 'delete'
        ? 'Delete Documents'
        : pendingConfirm === 'removeCategory'
          ? 'Remove Categories'
          : 'Reset Documents',
    message:
      pendingConfirm === 'delete'
        ? `Are you sure you want to delete ${selectedIds.size} documents? This action cannot be undone.`
        : pendingConfirm === 'removeCategory'
          ? `This clears the category on ${selectedIds.size} selected document${selectedIds.size === 1 ? '' : 's'}. This cannot be undone.`
          : `Are you sure you want to reset ${depthDescriptions[resetDepth]} for selected documents? This action cannot be undone.`,
    confirmLabel: pendingConfirm === 'delete' ? 'Delete' : pendingConfirm === 'removeCategory' ? 'Remove' : 'Reset',
    pendingLabel:
      pendingConfirm === 'delete' ? 'Deleting…' : pendingConfirm === 'removeCategory' ? 'Removing…' : 'Resetting…',
    isPending: pendingConfirm === 'delete' ? isDeleting : pendingConfirm === 'removeCategory' ? isUpdatingCategory : isResetting,
    onConfirm: () => {
      if (pendingConfirm === 'delete') {
        void confirmDeleteSelected();
      } else if (pendingConfirm === 'removeCategory') {
        void confirmRemoveCategory();
      } else {
        void confirmResetSelected();
      }
    },
    onClose: () => setPendingConfirm(null),
  };

  const handleUpdateCategory = async () => {
    if (selectedIds.size === 0 || !selectedCategory) return;

    try {
      setIsUpdatingCategory(true);
      setCategoryError(null);

      await Promise.all(
        Array.from(selectedIds).map((id) => backend.updateDocument(id, { category: selectedCategory })),
      );

      await fetchDocuments();
      setSelectedCategory('');
    } catch (error) {
      console.error('Failed to update categories:', error);
      setCategoryError('Failed to update categories');
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  const handleRemoveCategory = () => {
    if (selectedIds.size === 0) return;
    setPendingConfirm('removeCategory');
  };

  const selectedDocumentsTags = useMemo(() => {
    const tagSet = new Set<string>();
    documents
      .filter((doc) => selectedIds.has(doc.id))
      .forEach((doc) => {
        doc.tags?.forEach((tag) => tagSet.add(tag));
      });
    return Array.from(tagSet).sort();
  }, [documents, selectedIds]);

  return {
    documents,
    loading,
    hasNextPage,
    filters,
    setFilters,
    selectedIds,
    setSelectedIds,
    lastFetched,
    isDangerZoneExpanded,
    setIsDangerZoneExpanded,
    isDeleting,
    deleteError,
    selectedCategory,
    setSelectedCategory,
    isUpdatingCategory,
    categoryError,
    resetDepth,
    setResetDepth,
    isRetryingFailedSteps,
    setIsRetryingFailedSteps,
    fetchDocuments,
    handleResetSelected,
    handleDeleteSelected,
    confirmDialogProps,
    handleUpdateCategory,
    handleRemoveCategory,
    selectedDocumentsTags,
    loadMore,
    isResetting,
    resetError,
  };
}
